import { CardTypeEnum } from '@varo/graphql-schema-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import Button, { ButtonProps } from 'shared/components/vxl/Button/Button';
import { TagProps } from 'shared/components/vxl/Tag/Tag';
import theme from 'shared/styles/theme';

import { CardBack } from './CardBack';
import { CardFront } from './CardFront';
import CardFrozenVisual from './CardFrozenVisual';

export interface DigitalCardProps {
  /**
   * card type  CardTypeEnum.
   */
  cardType: CardTypeEnum;
  /**
   * first name card  text.
   */
  firstNameText: string;
  /**
   * last name card  text.
   */
  lastNameText: string;
  /**
   * card number text.
   */
  cardNumberText: string;
  /**
   * card expiration label.
   */
  expirationLabel: string;
  /**
   * card expiration date text.
   */
  expirationDateText: string;
  /**
   * card cvv label.
   */
  cvvLabel: string;
  /**
   * card cvv number text.
   */
  cvvNumberText: string;
  /**
   * card accept Tag component props as an argument. Use `ERROR` tag type.
   */
  cardTag?: TagProps;
  /**
   * canGetInfo boolean.
   */
  canGetInfo?: boolean;
  /**
   * show if card is suspended boolean.
   */
  isSuspended?: boolean;
  /**
   * showCardInfo if true, we are showing back of the card with card number (copy image), expiration date and cvv number.
   */
  showCardInfo: boolean;
  /**
   * Button prop accept Button component props as an argument. Use `Secondary` button type for InlineAlert.
   */
  button: Omit<ButtonProps, 'label'>;
  /**
   * show disabled card if isLocked or isExpired
   */
  isLocked?: boolean;
  isExpired?: boolean;
  handleCopyToClipboard: () => void;
  /**
   * show button loading if cvvFetching is true.
   */
  cvvFetching: boolean;
  /**
   * show if card is flipped.
   */
  isCardFlipped?: (boolean) => void;
  defaultToBack?: boolean;

  isFrozenLanguageEnabled?: boolean;
}

export const DigitalCard = (props: DigitalCardProps) => {
  const { t } = useTranslation('componentsStrings', {
    keyPrefix: 'digitalCardStrings',
  });
  const rotation = useSharedValue(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [toggle, setToggle] = useState(false);

  const [isAnimating, setIsAnimating] = useState(false);

  const isFrozenLanguageEnabled = props.isFrozenLanguageEnabled ?? false;

  const regularCardAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ rotateY: `${rotation.value}deg` }],
    zIndex: isFlipped ? 0 : 1,
    backfaceVisibility: 'hidden',
  }));
  const flippedCardAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ rotateY: `${rotation.value + 180}deg` }],
    zIndex: isFlipped ? 2 : 0,
    backfaceVisibility: 'hidden',
  }));

  const toggleCard = useCallback(() => {
    if (!props.isLocked || !props.isExpired) {
      props?.button?.onPress?.();
      setToggle(!toggle);
      setIsAnimating(true);
      if (props.isCardFlipped) {
        props.isCardFlipped(toggle);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isLocked, props.isExpired, props?.button, toggle]);

  useEffect(() => {
    if (!props.cvvFetching && props.cvvNumberText) {
      setIsFlipped(!isFlipped);
      rotation.value = withTiming(
        isFlipped ? 0 : 180,
        {
          duration: 500,
          easing: Easing.ease,
        },
        () => runOnJS(setIsAnimating)(false),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.cvvNumberText, toggle]);

  useEffect(() => {
    setIsFlipped(false);
    rotation.value = withTiming(0, {
      duration: 500,
      easing: Easing.ease,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isLocked, props.isExpired]);

  useEffect(() => {
    if (props.defaultToBack) {
      setIsFlipped(true);
      rotation.value = 180;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.defaultToBack]);

  const showFrozenIcon =
    props.isLocked && isFrozenLanguageEnabled && !props.isExpired;

  return (
    <>
      <Pressable style={styles.container} onPress={toggleCard}>
        <Animated.View
          style={[styles.regularCard, regularCardAnimatedStyle]}
          testID="card-front-container"
          aria-hidden={isFlipped}
        >
          <View
            style={props.isLocked || props.isExpired ? styles.locked : null}
          >
            <CardFront
              cardType={props.cardType}
              firstNameText={props.firstNameText}
              lastNameText={props.lastNameText}
            />
          </View>
          {showFrozenIcon ? (
            <View style={styles.frozenIconStyle}>
              <CardFrozenVisual iconColor="white" iconSize="MD" />
            </View>
          ) : null}
        </Animated.View>
        <Animated.View
          style={[styles.flippedCard, flippedCardAnimatedStyle]}
          testID="card-back-container"
          aria-hidden={!isFlipped}
        >
          <CardBack
            cardType={props.cardType}
            firstNameText={props.firstNameText}
            lastNameText={props.lastNameText}
            cardNumberText={props.cardNumberText}
            expirationLabel={props.expirationLabel}
            expirationDateText={props.expirationDateText}
            cvvLabel={props.cvvLabel}
            cvvNumberText={props.cvvNumberText}
            handleCopyToClipboard={props.handleCopyToClipboard}
            disabled={isAnimating}
            isFlipped={isFlipped}
          />
        </Animated.View>
      </Pressable>
      {props.isLocked || props.isExpired ? null : (
        <View style={styles.button}>
          <Button
            label={
              isFlipped
                ? t('flipCardButton.backLabel')
                : t('flipCardButton.frontLabel')
            }
            type="SECONDARY"
            onPress={toggleCard}
            isLoading={props.cvvFetching}
            isDisabled={props.button.isDisabled}
            {...Button}
          />
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  button: {
    padding: theme.spacing.m,
    width: '100%',
  },
  container: {
    height: 343,
    margin: theme.spacing.m,
    position: 'relative',
  },
  flippedCard: {
    alignItems: 'center',
    backfaceVisibility: 'hidden',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 2,
  },
  frozenIconStyle: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  locked: {
    opacity: 0.6,
  },
  regularCard: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 1,
  },
});

export default DigitalCard;
